import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { NewHire } from '../../services/hireService';
import { updateNewHire } from '../../services/hireService';

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  hire: NewHire | null;
  onSave?: (updatedNotes: string) => void;
}

const NotesDialog: React.FC<NotesDialogProps> = ({ open, onClose, hire, onSave }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && hire) {
      setNotes(hire.notes || '');
      setError(null);
      setSuccess(false);
    }
  }, [open, hire]);

  const handleSave = async () => {
    if (!hire) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      await updateNewHire(hire.id, { notes });
      
      setSuccess(true);
      
      // Call onSave callback if provided with the updated notes
      if (onSave) {
        onSave(notes);
      }

      // Close after a brief delay to show success
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 500);
    } catch (err: any) {
      console.error('Error saving notes:', err);
      setError(err.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setNotes('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Notes for {hire?.fullName || 'Hire'}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Notes saved successfully!
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this hire..."
            disabled={saving || loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Notes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesDialog;

